import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const VowelConsonantGame = ({ gameData, onComplete }) => {
    const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);

    const currentLetter = gameData?.letters?.[currentLetterIndex];

    const handleChoice = (categoryId) => {
        if (!currentLetter || feedback) return;
        triggerHaptic(25);

        const isCorrect = currentLetter.type === categoryId;

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_letter || 10;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                if (currentLetterIndex + 1 >= gameData.letters.length) {
                    onComplete?.(newScore);
                } else {
                    setCurrentLetterIndex(prev => prev + 1);
                }
            }, 2500);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
            }, 1500);
        }
    };

    if (!gameData || !currentLetter) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-9xl mb-8">🔤</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Ruošiamos raidės...</h3>
            </div>
        );
    }

    const progress = (currentLetterIndex / gameData.letters.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-indigo-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🔤
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Balsės ar Priebalsės?
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Taškai</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Raidė</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentLetterIndex + 1}/{gameData.letters.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-600 rounded-full transition-all duration-1000"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="flex flex-col items-center justify-center py-4 md:py-12">
            <div className="premium-glass rounded-[2rem] md:rounded-[4rem] p-8 md:p-16 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center w-full max-w-md">
                <div className="absolute top-0 right-0 p-4 md:p-8 text-6xl md:text-8xl opacity-[0.03] select-none pointer-events-none font-black italic">ABC</div>
                
                <div className="relative group mb-8">
                    <div className="absolute -inset-10 bg-indigo-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                    <div 
                        className={`
                            text-7xl md:text-[180px] font-black leading-none drop-shadow-xl relative z-10 transition-all duration-500
                            ${feedback === 'correct' ? 'scale-125 text-emerald-500' : feedback === 'wrong' ? 'animate-shake text-rose-500' : 'text-slate-800'}
                        `}
                    >
                        {currentLetter.letter}
                    </div>
                </div>

                <div className="text-center space-y-4 md:space-y-6">
                    <div className="text-6xl md:text-8xl drop-shadow-xl animate-float">{currentLetter.emoji}</div>
                    <div className="bg-white/80 px-6 py-2 md:px-10 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-indigo-100 shadow-inner">
                        <span className="text-sm md:text-3xl font-black text-slate-700 uppercase">{currentLetter.example_word}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-6 w-full p-4 md:p-6">
            <button
                onClick={() => handleChoice('vowel')}
                disabled={!!feedback}
                className={`
                    premium-button-3d rounded-2xl md:rounded-[3.5rem] p-4 md:p-12 flex flex-col items-center justify-center text-center transition-all duration-500 border-b-8 md:border-b-[12px]
                    bg-gradient-to-br from-rose-400 to-pink-600 border-rose-800 shadow-lg
                    hover:scale-105 active:translate-y-2 active:border-b-0 disabled:opacity-40
                `}
            >
                <div className="text-4xl md:text-7xl mb-2 md:mb-4 animate-float">🅰️</div>
                <h4 className="text-xs md:text-3xl font-black text-white uppercase tracking-tighter leading-none">Balsės</h4>
            </button>

            <button
                onClick={() => handleChoice('consonant')}
                disabled={!!feedback}
                className={`
                    premium-button-3d rounded-2xl md:rounded-[3.5rem] p-4 md:p-12 flex flex-col items-center justify-center text-center transition-all duration-500 border-b-8 md:border-b-[12px]
                    bg-gradient-to-br from-indigo-500 to-blue-700 border-indigo-800 shadow-lg
                    hover:scale-105 active:translate-y-2 active:border-b-0 disabled:opacity-40
                `}
            >
                <div className="text-4xl md:text-7xl mb-2 md:mb-4 animate-float [animation-delay:0.2s]">🅿️</div>
                <h4 className="text-xs md:text-3xl font-black text-white uppercase tracking-tighter leading-none">Priebalsės</h4>
            </button>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[3rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🌟' : '🤔'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLU!' : 'KLAIDA!'}
                            </h2>
                            <div className={`p-4 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-lg font-bold text-slate-700 leading-relaxed text-center">
                                    {feedback === 'correct' ? `Raidė "${currentLetter.letter}" tikrai yra ${currentLetter.type === 'vowel' ? 'balsė' : 'priebalsė'}!` : `Oi! Panašu, kad raidė "${currentLetter.letter}" gyvena kitame namelyje.`}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VowelConsonantGame;
